/**
 * NeuNexus — Industrial Innovation Platform Homepage.
 *
 * Sections:
 *   1. Hero (N logo, tagline, primary CTAs)
 *   2. Executive Dashboard (5 KPI cards + status strip)
 *   3. Ecosystem Map (visual hierarchy: NeuNexus → NeuGreen → Partners → Smart Factory)
 *   4. NeuGreen Energy — First Business Division
 *   5. Strategic Partner Center (Active + Proposed)
 *   6. Industrial Roadmap (4 phases)
 *   7. Technology Transfer
 *   8. Germany Positioning
 *   9. HX Assistant + Document Center CTAs
 */
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight, Cpu, Zap, Battery, SunMedium, Boxes, Radar, ShieldCheck,
  Sparkles, Building2, MapPin, GraduationCap, Wrench, Users, Layers,
  ChevronRight, Globe, Cog, Bot, Factory, FileText, Search, X,
} from "lucide-react";

const P = {
  bg:      "#080B12",
  bg2:     "#0A0F1A",
  surface: "rgba(255,255,255,0.03)",
  glass:   "rgba(255,255,255,0.045)",
  border:  "rgba(255,255,255,0.08)",
  ink:     "#E8ECF0",
  dim:     "rgba(232,236,240,0.62)",
  silver:  "#C0C7CE",
  green:   "#22C55E",   // NeuGreen accent
  blue:    "#3B82F6",   // NeuNexus accent
  amber:   "#F59E0B",
  cyan:    "#06B6D4",   // Future Manufacturing accent
  purple:  "#A855F7",
  yellow:  "#EAB308",
};

// -------------------------------------------------------- i18n

// i18n comes from the shared module — LangProvider must wrap the app in App.js
import { T, useT, LanguageSwitcher } from "@/i18n/neunexus";
import { partnerBySlug, SUPPLIERS, STRATEGIC_GROUPS, groupById } from "@/data/partners";
import RobotAssistedAssembly from "@/pages/bsgotech/RobotAssistedAssembly";
import NeuGreenFutureManufacturing, { useFutureManufacturingNav } from "@/pages/bsgotech/NeuGreenFutureManufacturing";


// -------------------------------------------------------- shared bits

const Section = ({ children, className = "", id }) => (
  <section id={id} className={"relative py-16 lg:py-24 " + className}>
    <div className="max-w-7xl mx-auto px-6 lg:px-10">{children}</div>
  </section>
);

const Eyebrow = ({ children, color }) => (
  <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: color || P.blue }}>
    {children}
  </div>
);

const H2 = ({ children }) => (
  <h2 className="mt-3 font-bold tracking-tight text-[36px] lg:text-[48px] leading-[1.05]" style={{ color: "#fff" }}>
    {children}
  </h2>
);

const P_ = ({ children, className = "" }) => (
  <p className={"mt-4 text-[14px] lg:text-[15px] leading-relaxed max-w-2xl " + className} style={{ color: P.dim }}>
    {children}
  </p>
);

const Card = ({ children, className = "", accent, hover = true }) => (
  <div
    className={"relative rounded-lg p-6 transition " + (hover ? "hover:border-white/20" : "") + " " + className}
    style={{
      background: P.glass,
      border: `1px solid ${P.border}`,
      backdropFilter: "blur(14px)",
      WebkitBackdropFilter: "blur(14px)",
    }}
  >
    {accent && <span className="absolute inset-x-6 -top-px h-px" style={{ background: `linear-gradient(90deg, transparent 0%, ${accent} 50%, transparent 100%)` }} />}
    {children}
  </div>
);

// -------------------------------------------------------- 0. HOME SEARCH

const HomeSearch = () => {
  const { t } = useT();
  const [q, setQ] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const wrapRef = React.useRef(null);
  const inputRef = React.useRef(null);

  // Close on outside click
  React.useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Global "/" shortcut to focus search
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "/" && document.activeElement?.tagName !== "INPUT" && document.activeElement?.tagName !== "TEXTAREA") {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const results = React.useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return [];
    return SUPPLIERS.filter((s) => {
      return (
        s.name.toLowerCase().includes(term) ||
        (s.category || "").toLowerCase().includes(term) ||
        (s.country || "").toLowerCase().includes(term) ||
        (s.description || "").toLowerCase().includes(term) ||
        (s.status || "").toLowerCase().includes(term) ||
        (s.tags || []).some((tag) => tag.toLowerCase().includes(term))
      );
    }).slice(0, 8);
  }, [q]);

  // Group results by strategic group
  const grouped = React.useMemo(() => {
    const map = new Map();
    for (const r of results) {
      if (!map.has(r.group)) map.set(r.group, []);
      map.get(r.group).push(r);
    }
    return Array.from(map.entries());
  }, [results]);

  return (
    <div
      ref={wrapRef}
      className="fixed top-4 left-1/2 -translate-x-1/2 z-50 w-[min(520px,calc(100vw-9rem))]"
      data-testid="home-search"
    >
      <div
        className={`flex items-center gap-2 rounded-full transition ${open ? "px-3.5 py-2" : "px-3 py-2"}`}
        style={{
          background: "rgba(8,11,18,0.72)",
          border: `1px solid ${open ? "rgba(34,197,94,0.55)" : P.border}`,
          backdropFilter: "blur(12px)",
        }}
      >
        <Search size={13} style={{ color: open ? P.green : P.silver }} className="shrink-0" />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={t.search.placeholder}
          data-testid="home-search-input"
          className="flex-1 bg-transparent outline-none text-[12.5px] placeholder:text-[rgba(228,231,235,0.4)]"
          style={{ color: P.ink }}
        />
        {q && (
          <button
            type="button"
            onClick={() => { setQ(""); inputRef.current?.focus(); }}
            data-testid="home-search-clear"
            className="w-5 h-5 rounded-full flex items-center justify-center hover:bg-white/10 transition"
            style={{ color: P.silver }}
            aria-label="Clear search"
          >
            <X size={11} />
          </button>
        )}
        {!q && (
          <kbd
            className="hidden sm:inline-flex items-center justify-center text-[9.5px] font-mono px-1.5 py-0.5 rounded"
            style={{ background: "rgba(255,255,255,0.05)", color: P.silver, border: `1px solid ${P.border}` }}
            aria-hidden="true"
          >
            /
          </kbd>
        )}
      </div>

      {open && q && (
        <div
          className="mt-2 rounded-lg overflow-hidden shadow-2xl"
          style={{
            background: "rgba(10,15,26,0.96)",
            border: `1px solid ${P.border}`,
            backdropFilter: "blur(16px)",
          }}
          data-testid="home-search-results"
        >
          {results.length === 0 ? (
            <div className="px-4 py-6 text-center">
              <div className="text-[11.5px]" style={{ color: P.dim }}>
                {t.search.noResults} <span style={{ color: "#fff" }}>&ldquo;{q}&rdquo;</span>
              </div>
            </div>
          ) : (
            <div className="max-h-[70vh] overflow-y-auto py-1">
              {grouped.map(([gid, items]) => {
                const g = groupById(gid);
                return (
                  <div key={gid} data-testid={`home-search-group-${gid}`}>
                    <div
                      className="px-4 pt-3 pb-1 text-[9.5px] uppercase tracking-[0.24em] font-semibold"
                      style={{ color: g?.accent || P.silver }}
                    >
                      {g?.label || "Partners"}
                    </div>
                    {items.map((s) => (
                      <Link
                        key={s.id}
                        to={`/partners/${s.id}`}
                        onClick={() => { setOpen(false); setQ(""); }}
                        data-testid={`home-search-result-${s.id}`}
                        className="flex items-center gap-3 px-4 py-2.5 transition hover:bg-white/[0.04]"
                      >
                        {s.logo ? (
                          <div
                            className="w-8 h-8 rounded flex items-center justify-center shrink-0"
                            style={{ background: "rgba(255,255,255,0.94)" }}
                          >
                            <img src={s.logo} alt="" className="max-h-6 max-w-6 object-contain" />
                          </div>
                        ) : (
                          <div
                            className="w-8 h-8 rounded flex items-center justify-center shrink-0 text-[11px] font-bold"
                            style={{ background: `${g?.accent || P.blue}22`, color: g?.accent || P.blue }}
                          >
                            {s.name[0]}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <div className="text-[12.5px] font-semibold truncate" style={{ color: "#fff" }}>{s.name}</div>
                            {s.status && s.status.toLowerCase().includes("proposed") && (
                              <span
                                className="text-[8.5px] uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0"
                                style={{ background: `${P.amber}18`, color: P.amber, border: `1px solid ${P.amber}44` }}
                              >
                                Proposed
                              </span>
                            )}
                          </div>
                          <div className="text-[10.5px] truncate" style={{ color: P.dim }}>
                            {s.category} · {s.country}
                          </div>
                        </div>
                        <ArrowUpRight size={12} style={{ color: P.silver }} className="shrink-0" />
                      </Link>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// -------------------------------------------------------- Back to BSGOTECH

const BackToBsgotech = () => (
  <Link
    to="/"
    data-testid="back-to-bsgotech"
    aria-label="Back to BSGOTECH"
    className="fixed top-4 left-4 z-50 flex items-center gap-2 px-3 py-1.5 rounded-full text-[10.5px] uppercase tracking-[0.28em] font-semibold transition hover:bg-white/[0.06]"
    style={{
      background: "rgba(8,11,18,0.72)",
      color: P.silver,
      border: `1px solid ${P.border}`,
      backdropFilter: "blur(12px)",
    }}
  >
    <ChevronRight size={11} className="rotate-180" />
    <img src="/bsgotech/logo-official.png" alt="" className="h-3.5 w-auto" aria-hidden="true" />
    <span className="hidden sm:inline">BSGOTECH</span>
  </Link>
);

// -------------------------------------------------------- 1. HERO

const Hero = () => {
  const { t } = useT();
  const futureNavLabel = useFutureManufacturingNav();
  return (
  <section className="relative min-h-screen flex items-center overflow-hidden" style={{ background: P.bg }}>
    {/* Ambient gradient blobs */}
    <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full" style={{ background: `radial-gradient(circle, ${P.blue}22 0%, transparent 70%)` }} />
    <div className="absolute -bottom-40 -right-40 w-[700px] h-[700px] rounded-full" style={{ background: `radial-gradient(circle, ${P.green}22 0%, transparent 70%)` }} />
    <div className="absolute inset-0" style={{
      backgroundImage: `linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)`,
      backgroundSize: "80px 80px",
      maskImage: "radial-gradient(circle at 50% 40%, black 30%, transparent 80%)",
      WebkitMaskImage: "radial-gradient(circle at 50% 40%, black 30%, transparent 80%)",
    }} />

    <div className="relative w-full max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-16 grid lg:grid-cols-[1.15fr_1fr] gap-12 items-center">
      <div>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10.5px] uppercase tracking-[0.28em] font-semibold" style={{ background: `${P.green}12`, color: P.green, border: `1px solid ${P.green}44` }}>
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: P.green }} /> {t.hero.badge}
          </div>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
          className="mt-6 font-semibold tracking-tight text-[44px] sm:text-[52px] lg:text-[64px] leading-[1.02]"
          style={{ color: "#fff", letterSpacing: "-0.02em" }}
          data-testid="hero-title"
        >
          Neu<span style={{ color: P.green }}>Nexus</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-5 text-[16px] lg:text-[18px] font-light max-w-xl leading-snug"
          style={{ color: P.ink }}
        >
          {t.hero.tagline}
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.14 }}
          className="mt-4 text-[13px] max-w-xl leading-relaxed"
          style={{ color: P.dim }}
        >
          {t.hero.description}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.18 }}
          className="mt-10 flex flex-wrap gap-3"
        >
          <a href="#ecosystem" data-testid="hero-cta-ecosystem"
            className="inline-flex items-center gap-2 px-5 py-3 text-[12.5px] font-semibold rounded-md"
            style={{ background: "#fff", color: P.bg }}>
            {t.hero.ctaExplore} <ArrowUpRight size={13} />
          </a>
          <a href="#neugreen" data-testid="hero-cta-neugreen"
            className="inline-flex items-center gap-2 px-5 py-3 text-[12.5px] font-semibold rounded-md"
            style={{ background: `${P.green}18`, color: P.green, border: `1px solid ${P.green}55` }}>
            {t.hero.ctaNeuGreen} <ChevronRight size={13} />
          </a>
          <a href="#partners" data-testid="hero-cta-partners"
            className="inline-flex items-center gap-2 px-5 py-3 text-[12.5px] font-semibold rounded-md"
            style={{ background: P.glass, color: P.ink, border: `1px solid ${P.border}` }}>
            {t.hero.ctaPartners} <ChevronRight size={13} />
          </a>
          <a href="#future-manufacturing" data-testid="hero-cta-future"
            className="inline-flex items-center gap-2 px-5 py-3 text-[12.5px] font-semibold rounded-md"
            style={{ background: `${P.cyan}18`, color: P.cyan, border: `1px solid ${P.cyan}55` }}>
            {futureNavLabel} <ChevronRight size={13} />
          </a>
        </motion.div>
      </div>

      {/* Right column — N logo showcase */}
      <motion.div
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className="relative flex items-center justify-center"
      >
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-[420px] h-[420px] rounded-full" style={{ background: `radial-gradient(circle, ${P.green}25 0%, transparent 65%)` }} />
        </div>
        <img
          src="/neunexus/logo-official.png"
          alt="NeuNexus"
          className="relative w-full max-w-md"
          style={{ filter: `drop-shadow(0 24px 60px ${P.green}66)` }}
          data-testid="hero-logo"
        />
      </motion.div>
    </div>
  </section>
  );
};

// -------------------------------------------------------- 2. ECOSYSTEM VISION (strategic full-width image)

const VISION_ARC_TOKENS = [
  { icon: SunMedium, color: P.green,  glow: true,  target: "/report#chapter-3"  }, // Energia — Integrated Solution
  { icon: Cpu,       color: P.blue,   glow: false, target: "/report#chapter-5"  }, // Digital Intelligence — Technical Architecture
  { icon: Radar,     color: P.amber,  glow: false, target: "/report#chapter-6"  }, // Precision Manufacturing — Smart Factory Workflow
  { icon: Factory,   color: P.purple, glow: false, target: "/report#chapter-11" }, // Future Smart Factory — Growth Roadmap
];

const VisionArc = () => {
  const { t } = useT();
  const arc = t.vision.arc || [];
  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 pt-6" data-testid="vision-arc">
      <div className="text-center text-[10.5px] uppercase tracking-[0.28em]" style={{ color: P.silver }}>
        {t.vision.arcTitle || t.vision.overview}
      </div>

      {/* Horizontal timeline, connected by a gradient rail */}
      <div className="relative mt-8">
        <div
          className="hidden lg:block absolute left-[10%] right-[10%] top-[28px] h-px"
          style={{
            background: `linear-gradient(90deg, ${P.green}88 0%, ${P.blue}88 33%, ${P.amber}88 66%, ${P.purple}88 100%)`,
          }}
          aria-hidden="true"
        />
        <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-4">
          {arc.map((p, i) => {
            const tok = VISION_ARC_TOKENS[i] || VISION_ARC_TOKENS[0];
            const Icon = tok.icon;
            return (
              <li key={i} data-testid={`vision-arc-${i + 1}`}>
                <Link
                  to={tok.target}
                  data-testid={`vision-arc-${i + 1}-link`}
                  className="group relative flex flex-col items-center text-center px-2 py-3 -mx-2 -my-3 rounded-lg transition hover:bg-white/[0.03]"
                >
                  {/* Node */}
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center relative z-10 transition group-hover:-translate-y-0.5"
                    style={{
                      background: "rgba(8,11,18,0.94)",
                      border: `1.5px solid ${tok.color}${tok.glow ? "" : "88"}`,
                      boxShadow: tok.glow ? `0 0 0 4px ${tok.color}1e, 0 0 22px ${tok.color}66` : "none",
                    }}
                  >
                    <Icon size={22} style={{ color: tok.color }} strokeWidth={1.75} />
                  </div>
                  {/* Number + status */}
                  <div className="mt-3 text-[9.5px] uppercase tracking-[0.28em] font-semibold" style={{ color: tok.color }}>
                    0{i + 1} · {p.status}
                  </div>
                  {/* Title */}
                  <div
                    className="mt-1 text-[14.5px] font-semibold tracking-tight"
                    style={{ color: "#fff", letterSpacing: "-0.005em" }}
                    data-testid={`vision-arc-${i + 1}-title`}
                  >
                    {p.title}
                  </div>
                  {/* Desc */}
                  <div className="mt-1.5 text-[11.5px] leading-relaxed max-w-[220px]" style={{ color: P.dim }}>
                    {p.desc}
                  </div>
                  {/* Read more hint on hover */}
                  <div
                    className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] font-semibold opacity-0 -translate-y-0.5 transition group-hover:opacity-100 group-hover:translate-y-0"
                    style={{ color: tok.color }}
                    aria-hidden="true"
                  >
                    {t.vision.arcReadMore || "Read the chapter"}
                    <ArrowUpRight size={11} />
                  </div>
                </Link>
              </li>
            );
          })}
        </ol>
      </div>

      <div className="mt-8 text-center text-[10.5px] uppercase tracking-[0.28em]" style={{ color: P.silver }}>
        {t.vision.arcAnchor}
      </div>
    </div>
  );
};

const EcosystemVision = () => {
  const { t } = useT();
  return (
  <section id="vision" className="relative border-t" style={{ background: P.bg, borderColor: P.border }}>
    <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-6 text-center">
      <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: P.green }}>{t.vision.eyebrow}</div>
      <h2 className="mt-3 font-semibold tracking-tight text-[24px] lg:text-[32px] leading-[1.15]" style={{ color: "#fff", letterSpacing: "-0.01em" }}>
        {t.vision.title}
      </h2>
      <p className="mt-4 max-w-2xl mx-auto text-[13.5px] leading-relaxed" style={{ color: P.dim }}>
        {t.vision.subtitle}
      </p>
    </div>
    <div className="relative w-full overflow-hidden" style={{ background: "#04070C" }}>
      <img
        src="/neunexus/hero-ecosystem-map.jpg"
        alt="NeuNexus Industrial Ecosystem Strategic Visualization"
        className="w-full h-auto block"
        style={{
          maskImage: "linear-gradient(180deg, transparent 0%, black 6%, black 94%, transparent 100%)",
          WebkitMaskImage: "linear-gradient(180deg, transparent 0%, black 6%, black 94%, transparent 100%)",
        }}
        data-testid="ecosystem-vision-image"
      />
      {/* Subtle glow overlay to keep the image feeling embedded in the page */}
      <div className="pointer-events-none absolute inset-0" style={{
        background: `radial-gradient(ellipse at center, transparent 40%, ${P.bg}66 100%)`,
      }} />
    </div>
    <VisionArc />
    <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-10 pb-20 text-center">
      <div className="text-[11.5px] uppercase tracking-[0.28em]" style={{ color: P.silver }}>
        {t.vision.overview}
      </div>
      <p className="mt-2 text-[12.5px] max-w-3xl mx-auto" style={{ color: P.dim }}>
        {t.vision.overviewSub}
      </p>
    </div>
  </section>
  );
};

// -------------------------------------------------------- 3. EXECUTIVE DASHBOARD

const KPI = ({ label, value, sub, color = P.blue, testid }) => (
  <Card accent={color}>
    <div className="text-[10px] uppercase tracking-[0.28em]" style={{ color: P.silver }}>{label}</div>
    <div className="mt-3 font-semibold text-[28px] lg:text-[34px] leading-none" style={{ color: "#fff" }} data-testid={testid}>{value}</div>
    <div className="mt-2 text-[11.5px]" style={{ color: P.dim }}>{sub}</div>
  </Card>
);

const Dashboard = () => {
  const { t } = useT();
  const k = t.dashboard.kpi;
  return (
  <Section id="dashboard" className="border-t" >
    <div className="mb-10">
      <Eyebrow color={P.blue}>{t.dashboard.eyebrow}</Eyebrow>
      <H2>{t.dashboard.title}</H2>
      <P_>{t.dashboard.subtitle}</P_>
    </div>
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
      <KPI label={k.partners.label}  value={k.partners.value}  sub={k.partners.sub}  color={P.blue}   testid="kpi-partners" />
      <KPI label={k.techAreas.label} value={k.techAreas.value} sub={k.techAreas.sub} color={P.purple} testid="kpi-tech-areas" />
      <KPI label={k.divisions.label} value={k.divisions.value} sub={k.divisions.sub} color={P.green}  testid="kpi-divisions" />
      <KPI label={k.stage.label}     value={k.stage.value}     sub={k.stage.sub}     color={P.amber}  testid="kpi-stage" />
      <KPI label={k.location.label}  value={k.location.value}  sub={k.location.sub}  color={P.silver} testid="kpi-location" />
    </div>
  </Section>
  );
};

// -------------------------------------------------------- 3. ECOSYSTEM MAP

const MapNode = ({ label, sub, color, big, testid }) => (
  <div
    data-testid={testid}
    className="rounded-lg text-center"
    style={{
      background: P.glass,
      border: `1px solid ${color}55`,
      boxShadow: `0 0 0 1px ${color}22, 0 12px 40px -12px ${color}55`,
      padding: big ? "22px 28px" : "16px 18px",
      minWidth: big ? 260 : 180,
    }}
  >
    <div className={big ? "text-[10.5px]" : "text-[9.5px]"} style={{ color: color, letterSpacing: "0.28em", textTransform: "uppercase", fontWeight: 700 }}>
      {label}
    </div>
    {sub && (
      <div className={big ? "mt-2 text-[13.5px] font-semibold" : "mt-1.5 text-[11.5px]"} style={{ color: "#fff" }}>
        {sub}
      </div>
    )}
  </div>
);

const Connector = () => <div className="w-px h-8 mx-auto" style={{ background: `linear-gradient(180deg, transparent 0%, ${P.silver}44 50%, transparent 100%)` }} />;

const EcosystemMap = () => {
  const { t } = useT();
  const n = t.map.nodes;
  return (
  <Section id="ecosystem" className="border-t" >
    <div className="mb-12 text-center">
      <Eyebrow color={P.green}>{t.map.eyebrow}</Eyebrow>
      <H2>{t.map.title}</H2>
      <p className="mt-4 mx-auto max-w-2xl text-[14px]" style={{ color: P.dim }}>
        {t.map.subtitle}
      </p>
    </div>

    <div className="flex flex-col items-center">
      <MapNode label="NeuNexus" sub={n.neunexus} color={P.green} big testid="map-neunexus" />
      <Connector />
      <MapNode label="NeuGreen Energy" sub={n.neugreen} color={P.green} big testid="map-neugreen" />
      <Connector />
      <div className="grid grid-cols-3 gap-4 lg:gap-8">
        <div className="flex flex-col items-center">
          <MapNode label={n.solar} sub="Bluesun" color={P.yellow} testid="map-bluesun" />
        </div>
        <div className="flex flex-col items-center">
          <MapNode label={n.storage} sub="Wattzing" color={P.green} testid="map-wattzing" />
        </div>
        <div className="flex flex-col items-center">
          <MapNode label={n.digital} sub="BENY" color={P.amber} testid="map-beny" />
        </div>
      </div>
      <Connector />
      <div className="grid grid-cols-3 gap-4 lg:gap-8">
        <MapNode label={n.digitalTwin} sub="Siemens" color={P.blue} testid="map-siemens" />
        <MapNode label={n.edge} sub="VCOW" color={P.blue} testid="map-vcow" />
        <MapNode label={n.precision} sub="DFOPTIX" color={P.purple} testid="map-dfoptix" />
      </div>
      <Connector />
      <MapNode label={n.smart} sub={n.smartSub} color={P.green} big testid="map-smart-factory" />
    </div>
  </Section>
  );
};

// -------------------------------------------------------- 4. NEUGREEN ENERGY

const ProductCard = ({ icon: Icon, title, tagline, bullets, color, testid }) => (
  <Card accent={color}>
    <div className="w-11 h-11 rounded-md flex items-center justify-center" style={{ background: `${color}18`, color }}>
      <Icon size={20} />
    </div>
    <div className="mt-4 text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color }}>{title}</div>
    <div className="mt-1.5 text-[16px] font-bold" style={{ color: "#fff" }} data-testid={testid}>{tagline}</div>
    <ul className="mt-3 space-y-1">
      {bullets.map((b, i) => (
        <li key={i} className="text-[12.5px] flex gap-2" style={{ color: P.ink }}>
          <span style={{ color }}>›</span> {b}
        </li>
      ))}
    </ul>
  </Card>
);

const NeuGreenSection = () => {
  const { t } = useT();
  const pr = t.neugreen.products;
  return (
  <Section id="neugreen" className="border-t">
    <div className="grid lg:grid-cols-[1fr_1.4fr] gap-12 items-start">
      <div>
        <Eyebrow color={P.green}>{t.neugreen.eyebrow}</Eyebrow>
        <H2>{t.neugreen.title}</H2>
        <p className="mt-4 text-[15px] font-light" style={{ color: P.ink }}>
          {t.neugreen.tagline}
        </p>
        <P_>{t.neugreen.description}</P_>
        <Link
          to="/neugreen"
          data-testid="neugreen-cta"
          className="mt-8 inline-flex items-center gap-2 px-5 py-3 text-[12.5px] font-semibold rounded-md"
          style={{ background: P.green, color: P.bg }}
        >
          {t.neugreen.cta} <ArrowUpRight size={13} />
        </Link>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <ProductCard icon={SunMedium} title={pr.solar.title}   tagline={pr.solar.tag}   bullets={pr.solar.bullets}   color={P.yellow} testid="neugreen-hx-solar" />
        <ProductCard icon={Battery}   title={pr.storage.title} tagline={pr.storage.tag} bullets={pr.storage.bullets} color={P.green}  testid="neugreen-hx-storage" />
        <ProductCard icon={Cpu}       title={pr.mgmt.title}    tagline={pr.mgmt.tag}    bullets={pr.mgmt.bullets}    color={P.blue}   testid="neugreen-hx-management" />
      </div>
    </div>
  </Section>
  );
};

// -------------------------------------------------------- 5. PARTNER CENTER

const PartnerRow = ({ name, role, contribution, status, variant, color, testid, slug }) => {
  const isActive = variant === "active";
  const badgeColor = isActive ? P.green : P.amber;
  const logo = slug ? partnerBySlug(slug)?.logo : null;
  const content = (
    <>
    {logo ? (
      <div
        className="w-14 h-14 rounded-md flex items-center justify-center shrink-0"
        style={{ background: "rgba(255,255,255,0.94)", border: `1px solid ${color}33` }}
        data-testid={`${testid}-logo`}
      >
        <img src={logo} alt={`${name} logo`} className="max-h-9 max-w-11 object-contain" />
      </div>
    ) : (
      <div
        className="w-14 h-14 rounded-md flex items-center justify-center shrink-0 font-bold text-[22px]"
        style={{ background: `${color}18`, color, border: `1px solid ${color}44` }}
        data-testid={`${testid}-initial`}
      >
        {name[0]}
      </div>
    )}
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-[15px] font-bold" style={{ color: "#fff" }}>{name}</div>
        {status && (
          <span className="text-[9px] uppercase tracking-[0.22em] px-2 py-0.5 rounded" style={{ background: `${badgeColor}18`, color: badgeColor, border: `1px solid ${badgeColor}44` }}>
            {status}
          </span>
        )}
      </div>
      <div className="mt-1 text-[10.5px] uppercase tracking-[0.22em]" style={{ color: color }}>{role}</div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {contribution.map((c) => (
          <span key={c} className="text-[10.5px] px-2 py-0.5 rounded" style={{ background: "rgba(255,255,255,0.03)", color: P.silver, border: `1px solid ${P.border}` }}>{c}</span>
        ))}
      </div>
    </div>
    <ArrowUpRight size={14} style={{ color: P.silver }} className="shrink-0 mt-1 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
    </>
  );
  const commonClass = "group rounded-lg p-5 flex items-start gap-5 transition hover:border-white/25";
  const commonStyle = { background: P.glass, border: `1px solid ${P.border}` };
  return slug ? (
    <Link to={`/partners/${slug}`} data-testid={testid} className={commonClass} style={commonStyle}>{content}</Link>
  ) : (
    <div data-testid={testid} className={commonClass} style={commonStyle}>{content}</div>
  );
};

const PartnerCenter = () => {
  const { t } = useT();
  const s = t.partners.statuses;
  const r = t.partners.roles;
  return (
  <Section id="partners" className="border-t">
    <div className="mb-10">
      <Eyebrow color={P.blue}>{t.partners.eyebrow}</Eyebrow>
      <H2>{t.partners.title}</H2>
      <P_>{t.partners.subtitle}</P_>
    </div>

    <div className="mb-10">
      <div className="text-[10px] uppercase tracking-[0.28em] font-semibold mb-4" style={{ color: P.green }}>{t.partners.activeLabel}</div>
      <div className="grid lg:grid-cols-3 gap-4">
        <PartnerRow name="Bluesun Solar"   slug="bluesun"  role={r.bluesun}  contribution={["OEM Manufacturing", "Solar Modules", "Private Label Production"]} status={s.proposed} color={P.yellow} testid="partner-bluesun" />
        <PartnerRow name="Wattzing"        slug="wattzing" role={r.wattzing} contribution={["Battery Systems", "Storage Technology", "Hybrid Inverters"]}      status={s.proposed} color={P.green}  testid="partner-wattzing" />
        <PartnerRow name="BENY New Energy" slug="beny"     role={r.beny}     contribution={["Electrical Protection", "Smart Components", "EV Charging Systems"]} status={s.proposed} color={P.amber}  testid="partner-beny" />
      </div>
    </div>

    <div>
      <div className="text-[10px] uppercase tracking-[0.28em] font-semibold mb-4" style={{ color: P.amber }}>{t.partners.futureLabel}</div>
      <div className="grid lg:grid-cols-3 gap-4">
        <PartnerRow name="Siemens"        slug="siemens"    role={r.siemens}    contribution={["Digital Twin", "Industrial Automation", "Xcelerator", "Smart Infrastructure"]} status={s.proposed} color={P.blue}   testid="partner-siemens" />
        <PartnerRow name="VCOW"           slug="vcow"       role={r.vcow}       contribution={["Industrial Edge Computing", "Industrial PCs", "AI Compute Infrastructure"]}   status={s.proposed} color={P.blue}   testid="partner-vcow" />
        <PartnerRow name="DFOPTIX"        slug="dfoptix"    role={r.dfoptix}    contribution={["Machine Vision", "Sensor Calibration", "Optical Inspection", "Metrology"]}   status={s.proposed} color={P.purple} testid="partner-dfoptix" />
        <PartnerRow name="Fraunhofer"     slug="fraunhofer" role={r.fraunhofer} contribution={["Technology Validation", "Pilot Projects", "Industrial Demonstrators"]}       status={s.research} color={P.yellow} testid="partner-fraunhofer" />
        <PartnerRow name="NEURA Robotics" slug="neura"      role={r.neura}      contribution={["Cognitive Robotics", "Humanoid Robots", "Human-Robot Collaboration"]}         status={s.proposed} color={P.amber}  testid="partner-neura" />
      </div>
    </div>
  </Section>
  );
};

// -------------------------------------------------------- 6. ROADMAP

const PhaseCard = ({ phase, title, bullets, color, current, testid, nowLabel }) => (
  <Card accent={color}>
    <div className="flex items-center justify-between">
      <div className="text-[10px] uppercase tracking-[0.28em] font-mono" style={{ color: P.silver }}>{phase}</div>
      {current && (
        <span className="text-[9px] uppercase tracking-[0.22em] px-1.5 py-0.5 rounded" style={{ background: `${color}22`, color, border: `1px solid ${color}55` }}>
          {nowLabel || "Now"}
        </span>
      )}
    </div>
    <div className="mt-3 text-[16px] font-bold leading-tight" style={{ color: "#fff" }} data-testid={testid}>{title}</div>
    <ul className="mt-4 space-y-1.5">
      {bullets.map((b, i) => (
        <li key={i} className="text-[12px] flex gap-2" style={{ color: P.ink }}>
          <span style={{ color }}>›</span> {b}
        </li>
      ))}
    </ul>
  </Card>
);

const Roadmap = () => {
  const { t } = useT();
  const ph = t.roadmap.phases;
  const colors = [P.green, P.blue, P.purple, P.amber];
  return (
  <Section id="roadmap" className="border-t">
    <div className="mb-10">
      <Eyebrow color={P.green}>{t.roadmap.eyebrow}</Eyebrow>
      <H2>{t.roadmap.title}</H2>
      <P_>{t.roadmap.subtitle}</P_>
    </div>
    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
      {ph.map((p, i) => (
        <PhaseCard key={i} phase={`Phase 0${i + 1}`} title={p.title} bullets={p.bullets} color={colors[i]} current={i === 0} testid={`phase-${i + 1}`} nowLabel={t.roadmap.now} />
      ))}
    </div>
  </Section>
  );
};

// -------------------------------------------------------- 6.5 STRATEGIC BLUEPRINT

const BLUEPRINT_DOC_ID = "0315c49b-d3fc-432b-a53e-1f98fb5ff042";
const BLUEPRINT_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") +
  `/api/ops/documents/${BLUEPRINT_DOC_ID}/download`;

const StrategicBlueprint = () => {
  const { t } = useT();
  const b = t.blueprint;
  return (
  <Section className="border-t">
    <Link
      to="/report"
      data-testid="strategic-blueprint-card"
      className="group relative block rounded-xl overflow-hidden transition hover:border-white/25"
      style={{
        background: `linear-gradient(135deg, ${P.amber}14 0%, ${P.bg2} 45%, ${P.purple}18 100%)`,
        border: `1px solid ${P.amber}44`,
      }}
    >
      <div className="grid lg:grid-cols-[1.35fr_1fr] gap-0">
        <div className="p-8 lg:p-12">
          <div className="flex items-center gap-2">
            <Factory size={16} style={{ color: P.amber }} />
            <div className="text-[10.5px] uppercase tracking-[0.32em] font-semibold" style={{ color: P.amber }}>
              {b.eyebrow}
            </div>
          </div>
          <h2 className="mt-4 font-semibold tracking-tight text-[28px] lg:text-[38px] leading-[1.08]" style={{ color: "#fff", letterSpacing: "-0.01em" }}>
            {b.title}
          </h2>
          <p className="mt-5 max-w-xl text-[13.5px] leading-relaxed" style={{ color: P.dim }}>
            {b.subtitle}
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {[b.pill1, b.pill2, b.pill3].map((label, i) => (
              <span
                key={i}
                data-testid={`blueprint-pill-${i}`}
                className="text-[10.5px] uppercase tracking-[0.2em] px-2.5 py-1 rounded"
                style={{ background: "rgba(255,255,255,0.04)", color: P.silver, border: `1px solid ${P.border}` }}
              >
                {label}
              </span>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-5">
            <span className="inline-flex items-center gap-2 text-[12.5px] font-semibold" style={{ color: P.amber }}>
              <FileText size={13} />
              {b.cta}
              <ArrowUpRight size={13} className="transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </span>
            <a
              href={BLUEPRINT_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              data-testid="strategic-blueprint-pdf"
              className="inline-flex items-center gap-1.5 text-[11.5px]"
              style={{ color: P.silver }}
            >
              PDF · Blueprint <ArrowUpRight size={11} />
            </a>
          </div>
          <div className="mt-3 text-[10.5px]" style={{ color: P.silver, opacity: 0.7 }}>
            {b.note}
          </div>
        </div>
        <div
          className="relative min-h-[220px] lg:min-h-[100%] flex items-center justify-center overflow-hidden"
          style={{
            background: `linear-gradient(135deg, ${P.bg} 0%, ${P.amber}22 55%, ${P.purple}33 100%)`,
            borderLeft: `1px solid ${P.border}`,
          }}
          aria-hidden="true"
        >
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 30% 30%, ${P.amber}33 0%, transparent 55%), radial-gradient(circle at 75% 70%, ${P.purple}33 0%, transparent 55%)`,
          }} />
          <div className="relative flex flex-col items-center gap-3">
            <div
              className="w-24 h-24 lg:w-28 lg:h-28 rounded-full flex items-center justify-center"
              style={{ background: `${P.amber}22`, border: `1px solid ${P.amber}66` }}
            >
              <Bot size={44} style={{ color: P.amber }} />
            </div>
            <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: P.silver }}>
              NeuNexus · Blueprint
            </div>
          </div>
        </div>
      </div>
    </Link>
  </Section>
  );
};

const TransferPill = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-2 px-3 py-2 rounded-md" style={{ background: P.glass, border: `1px solid ${P.border}` }}>
    <Icon size={13} style={{ color: P.green }} />
    <span className="text-[12px]" style={{ color: P.ink }}>{label}</span>
  </div>
);

const TechTransfer = () => {
  const { t } = useT();
  const icons = [GraduationCap, Wrench, Boxes, Cog, Layers, Users];
  return (
  <Section className="border-t">
    <div className="grid lg:grid-cols-2 gap-12 items-center">
      <div>
        <Eyebrow color={P.blue}>{t.transfer.eyebrow}</Eyebrow>
        <H2>{t.transfer.title}</H2>
        <P_>{t.transfer.description}</P_>
      </div>
      <div className="grid grid-cols-2 gap-2.5">
        {t.transfer.pills.map((p, i) => {
          const Icon = icons[i] || Layers;
          return <TransferPill key={i} icon={Icon} label={p} />;
        })}
      </div>
    </div>
  </Section>
  );
};

// -------------------------------------------------------- 8. GERMANY POSITIONING

const GermanyChip = ({ icon: Icon, label, sub }) => (
  <div className="rounded-md p-4" style={{ background: P.glass, border: `1px solid ${P.border}` }}>
    <div className="flex items-center gap-2">
      <Icon size={13} style={{ color: P.blue }} />
      <div className="text-[12px] font-semibold" style={{ color: "#fff" }}>{label}</div>
    </div>
    <div className="mt-1 text-[11px]" style={{ color: P.dim }}>{sub}</div>
  </div>
);

const Germany = () => {
  const { t } = useT();
  const icons = [Factory, Building2, Radar, ShieldCheck, Globe, Zap];
  return (
  <Section className="border-t">
    <div className="grid lg:grid-cols-[1fr_1.2fr] gap-12 items-center">
      <div>
        <Eyebrow color={P.amber}>{t.germany.eyebrow}</Eyebrow>
        <H2>{t.germany.title}</H2>
        <P_>{t.germany.description}</P_>
        <div className="mt-6 inline-flex items-center gap-2 px-3 py-2 rounded-md" style={{ background: P.glass, border: `1px solid ${P.border}` }}>
          <MapPin size={13} style={{ color: P.blue }} />
          <span className="text-[12px]" style={{ color: P.ink }}>{t.germany.location}</span>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {t.germany.chips.map((c, i) => {
          const Icon = icons[i] || Building2;
          return <GermanyChip key={i} icon={Icon} label={c.label} sub={c.sub} />;
        })}
      </div>
    </div>
  </Section>
  );
};

// -------------------------------------------------------- 9. HX ASSISTANT + DOC CENTER CTA

const CtaBanner = () => {
  const { t } = useT();
  return (
  <Section className="border-t">
    <div className="grid lg:grid-cols-2 gap-6">
      <Link
        to="/ops"
        data-testid="cta-assistant"
        className="group relative rounded-lg p-8 lg:p-10 overflow-hidden transition hover:border-white/25"
        style={{ background: `linear-gradient(135deg, ${P.blue}18 0%, ${P.bg2} 60%)`, border: `1px solid ${P.blue}55` }}
      >
        <Sparkles size={22} style={{ color: P.blue }} />
        <div className="mt-4 text-[10.5px] uppercase tracking-[0.28em] font-semibold" style={{ color: P.blue }}>{t.cta.assistant.eyebrow}</div>
        <div className="mt-2 text-[18px] lg:text-[22px] font-semibold leading-tight" style={{ color: "#fff" }}>
          {t.cta.assistant.title}
        </div>
        <div className="mt-3 text-[13px] max-w-lg" style={{ color: P.dim }}>
          {t.cta.assistant.desc}
        </div>
        <div className="mt-6 inline-flex items-center gap-2 text-[12px] font-semibold" style={{ color: P.blue }}>
          {t.cta.assistant.cta} <ArrowUpRight size={13} className="transition group-hover:translate-x-0.5" />
        </div>
      </Link>

      <Link
        to="/ops"
        data-testid="cta-documents"
        className="group relative rounded-lg p-8 lg:p-10 overflow-hidden transition hover:border-white/25"
        style={{ background: `linear-gradient(135deg, ${P.green}18 0%, ${P.bg2} 60%)`, border: `1px solid ${P.green}55` }}
      >
        <FileText size={22} style={{ color: P.green }} />
        <div className="mt-4 text-[10.5px] uppercase tracking-[0.28em] font-semibold" style={{ color: P.green }}>{t.cta.docs.eyebrow}</div>
        <div className="mt-2 text-[18px] lg:text-[22px] font-semibold leading-tight" style={{ color: "#fff" }}>
          {t.cta.docs.title}
        </div>
        <div className="mt-3 text-[13px] max-w-lg" style={{ color: P.dim }}>
          {t.cta.docs.desc}
        </div>
        <div className="mt-6 inline-flex items-center gap-2 text-[12px] font-semibold" style={{ color: P.green }}>
          {t.cta.docs.cta} <ArrowUpRight size={13} className="transition group-hover:translate-x-0.5" />
        </div>
      </Link>
    </div>
  </Section>
  );
};

// -------------------------------------------------------- Bottom message

const BottomMessage = () => {
  const { t } = useT();
  const colorMap = { blue: P.blue, green: P.green, silver: P.silver, white: "#fff" };
  return (
  <Section className="border-t">
    <div className="text-center max-w-3xl mx-auto">
      <div className="text-[10.5px] uppercase tracking-[0.34em] font-semibold" style={{ color: P.silver }}>{t.bottom.eyebrow}</div>
      <div className="mt-4 text-[16px] lg:text-[20px] font-light leading-relaxed" style={{ color: "#fff" }}>
        {t.bottom.lines.map((line, i) => (
          <React.Fragment key={i}>
            <span style={{ color: colorMap[line.color], fontWeight: line.color === "white" ? 600 : 400 }}>{line.text}</span>
            {i < t.bottom.lines.length - 1 && <br />}
          </React.Fragment>
        ))}
      </div>
    </div>
  </Section>
  );
};

// -------------------------------------------------------- ROOT

export default function NeuNexusHome() {
  const { lang } = useT();
  return (
    <div className="relative" style={{ background: P.bg, color: P.ink }} data-testid="page-home">
      <LanguageSwitcher />
      <BackToBsgotech />
      <HomeSearch />
      <Hero />
      <EcosystemVision />
      <Dashboard />
      <EcosystemMap />
      <NeuGreenSection />
      <PartnerCenter />
      <Roadmap />
      <StrategicBlueprint />
      <RobotAssistedAssembly />
      <NeuGreenFutureManufacturing />
      <TechTransfer />
      <Germany />
      <CtaBanner />
      <BottomMessage />
      <footer className="border-t py-10 text-center" style={{ borderColor: P.border }}>
        <div className="text-[10px] uppercase tracking-[0.32em]" style={{ color: P.silver }}>
          {T[lang].footer.line1}
        </div>
        <div className="mt-2 text-[11px]" style={{ color: P.dim }}>
          {T[lang].footer.line2}
        </div>
      </footer>
    </div>
  );
}
